import React from "react";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import PennText from "@/components/ui/text/PennText";

type PageHeaderProps = {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
};

export const PageHeader = ({ title, subtitle, action }: PageHeaderProps) => (
  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
    <div className="min-w-0">
      {/* Page title/subtitle sit on the navy canvas, not on a card, so they
          use the on-navy tokens (same as the customer selector headings). */}
      <PennText as="h1" font="semibold" color="on-navy" className="text-h1">
        {title}
      </PennText>
      {subtitle && (
        <PennText as="p" size="base" color="on-navy-secondary" className="mt-1">
          {subtitle}
        </PennText>
      )}
    </div>
    {action && <div className="flex flex-wrap items-center gap-2 sm:shrink-0">{action}</div>}
  </div>
);

export const AddButton = ({ onClick, label = "Add record" }: { onClick: () => void; label?: string }) => (
  <button
    onClick={onClick}
    className="inline-flex items-center gap-2 px-4 py-2 bg-penn-500 hover:bg-penn-400 active:bg-penn-500 text-white text-button rounded-lg shadow-sm shadow-penn-100 dark:shadow-penn-900/40 transition-all duration-150 cursor-pointer"
  >
    <Plus size={15} />
    {label}
  </button>
);

export const SaveButton = ({ onClick, label = "Save changes" }: { onClick?: () => void; label?: string }) => (
  <button
    type={onClick ? "button" : "submit"}
    onClick={onClick}
    className="inline-flex items-center gap-2 px-4 py-2 bg-penn-500 hover:bg-penn-400 active:bg-penn-500 text-white text-button rounded-lg shadow-sm shadow-penn-100 dark:shadow-penn-900/30 transition-all duration-150 cursor-pointer"
  >
    {label}
  </button>
);

export const CancelButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    className="px-4 py-2 border border-gray-200 dark:border-gray-700 text-button text-blue_grey2 bg-white dark:bg-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-150 cursor-pointer"
  >
    Cancel
  </button>
);

/**
 * Internal (staff) vs External (customer-app) pill — shared by the All Users
 * table (user origin) and the Roles Management table (role type).
 */
export const OriginBadge = ({ isInternal }: { isInternal: boolean }) => (
  <span
    className={`inline-flex items-center gap-2 px-3 py-0.5 rounded-full text-small-medium border ${
      isInternal
        ? "bg-penn-50 text-penn-700 border-penn-100 dark:bg-penn-900/40 dark:text-penn-400 dark:border-penn-700"
        : "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-400 dark:border-amber-800"
    }`}
  >
    {isInternal ? "Internal" : "External"}
  </span>
);

export const DeleteButton = ({
  onClick,
  disabled = false,
  disabledTitle = "Cannot be deleted",
}: {
  onClick: () => void;
  disabled?: boolean;
  disabledTitle?: string;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={disabled ? disabledTitle : "Delete"}
    aria-label="Delete"
    className={`inline-flex items-center justify-center p-2 rounded-lg transition-colors duration-150 ${
      disabled
        ? "text-blue_grey4 cursor-not-allowed"
        : "text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/40 cursor-pointer"
    }`}
  >
    <Trash2 size={18} />
  </button>
);

export const ViewButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    title="View"
    aria-label="View"
    className="inline-flex items-center justify-center p-2 rounded-lg text-penn-500 dark:text-penn-400 hover:bg-penn-100 dark:hover:bg-penn-900/40 transition-colors duration-150 cursor-pointer"
  >
    <Eye size={18} />
  </button>
);

export const EditButton = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    onClick={onClick}
    title="Edit"
    aria-label="Edit"
    className="inline-flex items-center justify-center p-2 rounded-lg text-penn-500 dark:text-penn-400 hover:bg-penn-100 dark:hover:bg-penn-900/40 transition-colors duration-150 cursor-pointer"
  >
    <Pencil size={18} />
  </button>
);
